import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

/**
 * AuthContext — v9.74: SEM localStorage.
 *
 * O token JWT vive APENAS em cookie HttpOnly (definido pelo backend em
 * `/api/auth/login`, flag `secure=true samesite=none`). O frontend não
 * tem acesso ao token nem via JavaScript nem via localStorage — bloqueia
 * ataques XSS de roubo de token.
 *
 * A sessão é validada exclusivamente por `GET /api/auth/me` no boot,
 * que lê o cookie automaticamente (via `withCredentials: true` no axios).
 * Se o cookie expirou/foi apagado, o usuário refaz login.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=user
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (error) {
      if (error?.response?.status !== 401) {
        console.error("Auth refresh error:", error);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // v9.77: listener global de sessão expirada. Interceptor do axios
    // dispara `auth-logout` quando `/auth/refresh` falhar (refresh_token
    // expirado, invalidado ou usuário deletado). Aqui derrubamos o estado
    // pra que <Layout> renderize o /login em vez dos pollers continuarem
    // batendo 401. Evita loop de 401 infinito.
    const onLogout = () => setUser(false);
    window.addEventListener("auth-logout", onLogout);
    return () => window.removeEventListener("auth-logout", onLogout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    // Cookie HttpOnly é setado pelo backend na resposta — nunca ficamos com
    // o token em memória do frontend. `data` contém só id/email/name/role.
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.warn("Logout endpoint falhou (sessão limpa localmente):", error?.message);
    }
    setUser(false);
  };

  // v9.79 (code review): memoiza o value pra não re-renderizar TODOS os
  // consumers a cada render deste provider. Sem isso, qualquer componente
  // que chama useAuth() re-renderiza mesmo sem `user` mudar.
  const value = useMemo(
    () => ({ user, loading, login, logout, refresh }),
    [user, loading]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  );
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
